import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ChartService } from '../../services/chart.service';
import { map, filter, mergeMap, catchError, of, from } from 'rxjs';
import {
  selectHealthIndicatorTrendEntry,
  selectTrendEntry,
  selectAlarmDetailsTrendEntry,
} from '../selectors/rule-result.selector';
import * as RuleResultActions from '../actions/rule-result.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { TrendCacheService } from '../../services/trend-cache.service';
import {
  HEALTH_INDICATOR_TTL_MS,
  TTL_MS,
} from '../../shared/constants/chart.constants';

@Injectable()
export class RuleResultEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private chartService: ChartService,
    private cache: TrendCacheService,
  ) {}

  loadIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadTrendIfNeeded),
      concatLatestFrom(({ uid, range }) =>
        this.store.select(selectTrendEntry(uid, range)),
      ),
      mergeMap(
        ([{ uid, range, alertThreshold, interpolationInMinutes }, entry]) => {
          const now = Date.now();
          const freshInStore =
            !!entry?.rawData &&
            !!entry?.fetchedAt &&
            now - (entry.fetchedAt ?? 0) < TTL_MS;
          if (freshInStore) return of({ type: '[Trend] Noop' });

          //1) Try fresh from IndexedDB
          return from(this.cache.get(uid, range)).pipe(
            map((cachedFresh) => {
              if (cachedFresh) {
                return RuleResultActions.loadTrendSuccess({
                  uid,
                  range,
                  rawData: cachedFresh.rawData,
                  fetchedAt: cachedFresh.fetchedAt,
                });
              }
              return RuleResultActions.loadTrend({
                uid,
                range,
                alertThreshold,
                interpolationInMinutes,
              });
            }),
          );
        },
      ),
      filter((a) => a.type !== '[Trend] Noop'),
    ),
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadTrend),
      mergeMap(
        ({ uid, range, interpolationInMinutes }) =>
          this.chartService
            .getChartData(uid, range, interpolationInMinutes)
            .pipe(
              map((rawData) => {
                const fetchedAt = Date.now();
                this.cache.set(uid, range, { rawData, fetchedAt });
                return RuleResultActions.loadTrendSuccess({
                  uid,
                  range,
                  rawData,
                  fetchedAt,
                });
              }),
              catchError((err) =>
                of(
                  RuleResultActions.loadTrendFailure({
                    uid,
                    range,
                    error: err?.message ?? 'Unknown error',
                  }),
                ),
              ),
            ),
        5,
      ),
    ),
  );

  loadHealthIndicatorIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadHealthIndicatorTrendIfNeeded),
      concatLatestFrom(({ uid, range }) =>
        this.store.select(selectHealthIndicatorTrendEntry(uid, range)),
      ),
      mergeMap(
        ([{ uid, range, alertThreshold, interpolationInMinutes }, entry]) => {
          const now = Date.now();
          const freshInStore =
            !!entry?.rawData &&
            !!entry?.fetchedAt &&
            now - (entry.fetchedAt ?? 0) < HEALTH_INDICATOR_TTL_MS;
          if (freshInStore) return of({ type: '[Trend] Noop' });
          return from(this.cache.getHealthIndicator(uid, range)).pipe(
            map((cachedFresh) => {
              if (cachedFresh) {
                return RuleResultActions.loadHealthIndicatorTrendSuccess({
                  uid,
                  range,
                  rawData: cachedFresh.rawData,
                  fetchedAt: cachedFresh.fetchedAt,
                });
              }
              return RuleResultActions.loadHealthIndicatorTrend({
                uid,
                range,
                alertThreshold,
                interpolationInMinutes,
              });
            }),
          );
        },
      ),
      filter((a) => a.type !== '[Trend] Noop'),
    ),
  );

  loadHealthIndicator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadHealthIndicatorTrend),
      mergeMap(
        ({ uid, range }) =>
          this.chartService.getHealthIndicatorChartData(uid).pipe(
            map((rawData) => {
              const fetchedAt = Date.now();
              this.cache.setHealthIndicator(uid, range, { rawData, fetchedAt });
              return RuleResultActions.loadHealthIndicatorTrendSuccess({
                uid,
                range,
                rawData,
                fetchedAt,
              });
            }),
            catchError((err) =>
              of(
                RuleResultActions.loadHealthIndicatorTrendFailure({
                  uid,
                  range,
                  error: err?.message ?? 'Unknown error',
                }),
              ),
            ),
          ),
        5,
      ),
    ),
  );

  loadAlarmDetailsIfNeeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadAlarmDetailsTrendIfNeeded),
      concatLatestFrom(({ uid, range }) =>
        this.store.select(selectAlarmDetailsTrendEntry(uid, range)),
      ),
      mergeMap(
        ([{ uid, range, alertThreshold, interpolationInMinutes }, entry]) => {
          const now = Date.now();
          const freshInStore =
            !!entry?.rawData &&
            !!entry?.fetchedAt &&
            now - (entry.fetchedAt ?? 0) < TTL_MS;
          if (freshInStore) return of({ type: '[Trend] Noop' });

          return from(this.cache.getAlarmDetails(uid, range)).pipe(
            map((cachedFresh) => {
              if (cachedFresh) {
                return RuleResultActions.loadAlarmDetailsTrendSuccess({
                  uid,
                  range,
                  rawData: cachedFresh.rawData,
                  fetchedAt: cachedFresh.fetchedAt,
                });
              }
              return RuleResultActions.loadAlarmDetailsTrend({
                uid,
                range,
                alertThreshold,
                interpolationInMinutes,
              });
            }),
          );
        },
      ),
      filter((a) => a.type !== '[Trend] Noop'),
    ),
  );

  loadAlarmDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RuleResultActions.loadAlarmDetailsTrend),
      mergeMap(
        ({ uid, range, interpolationInMinutes }) =>
          this.chartService
            .getChartData(uid, range, interpolationInMinutes)
            .pipe(
              map((rawData) => {
                const fetchedAt = Date.now();
                this.cache.setAlarmDetails(uid, range, { rawData, fetchedAt });
                return RuleResultActions.loadAlarmDetailsTrendSuccess({
                  uid,
                  range,
                  rawData,
                  fetchedAt,
                });
              }),
              catchError((err) =>
                of(
                  RuleResultActions.loadAlarmDetailsTrendFailure({
                    uid,
                    range,
                    error: err?.message ?? 'Unknown error',
                  }),
                ),
              ),
            ),
        5,
      ),
    ),
  );
}
